// The Notebook's data shapes (#1869).
//
// Cells are derived from the chat's plugin events (see notebookCells.ts); only
// what the events cannot hold is stored: the person's notes, their unsent cell
// edits, and their Confirm/Undo decisions on the agent's revisions. That stored
// part is NotebookState, one agentdb row per chat (`agent_notebooks`), mirrored
// from goapi/pkg/agentkit/agentdb/notebooks.go by hand.

/** The person's answer to one agent revision, keyed by the revision's event id. */
export type NotebookDecision = 'confirmed' | 'undone'

export interface NotebookNote {
  id: string
  text: string
  /** The cell this note sits after; null puts it at the top. */
  after: string | null
  created_at: number
}

/** The person's unsent change to a cell: SQL for a query, the axes for a table or chart. */
export interface NotebookEdit {
  sql?: string
  side?: string
  top?: string
  filter?: string
}

export interface NotebookState {
  version: number
  notes: NotebookNote[]
  decisions: Record<string, NotebookDecision>
  edits: Record<string, NotebookEdit>
}

export const EMPTY_NOTEBOOK_STATE: NotebookState = { version: 0, notes: [], decisions: {}, edits: {} }

export type NotebookCellKind = 'table' | 'chart' | 'composite' | 'query' | 'script' | 'note'

/** One event that revised a cell (it named the cell in its `revises` argument). */
export interface NotebookRevision {
  eventId: string
  kind: NotebookCellKind
  data: Record<string, unknown>
  decision?: NotebookDecision
}

export interface NotebookCell {
  /** The source event's id (pluginEventId), a script's path, or a note's id. */
  id: string
  kind: NotebookCellKind
  title: string
  /** The event payload the cell shows now: the original, or the last confirmed revision. */
  data: Record<string, unknown>
  /** The tool call that made the item, for "Open in chat". */
  toolCallId?: string
  /** The dataset name under data/ (tables, charts and composites saved there). */
  datasetName?: string
  /** Every revision the agent made, in order, with its decision. */
  revisions: NotebookRevision[]
  /** The newest revision the person has not decided on yet. */
  pending?: NotebookRevision
  /** The person's unsent edit. */
  edit?: NotebookEdit
  /** A later revision the person undid or has not decided on was saved to this
   *  cell's file (same dataset name, or a query's same saved path), so the file
   *  no longer holds what the cell shows. */
  fileReplaced?: boolean
  /** A table from a batch render carries no figures; it shows Run instead. */
  needsRun?: boolean
  /** Script cells: the composite the script came from. */
  sourceCellId?: string
  /** Note cells: the note's text. */
  text?: string
}
